import type { GatewayBrowserClient } from "../gateway.ts";
import type { SelfImprovementJobRecord } from "../types.ts";

function isUnknownSelfImprovementMethodError(err: unknown): boolean {
  return String(err).includes("unknown method: fleet.selfImprovement");
}

export function formatFleetSelfImprovementError(err: unknown): string {
  if (isUnknownSelfImprovementMethodError(err)) {
    return "Self improvement needs a newer gateway. Run pnpm build, restart the gateway, and refresh.";
  }
  const msg = String(err);
  if (msg.includes("disabled in the OTGS workspace")) {
    return 'YouTrack ticket creation and Fleet self-improvement are disabled in the OTGS workspace. Uncheck "Self improvement" or switch to the prizm profile.';
  }
  if (msg.includes("fleet project not found") || msg.includes("fleet project belongs to agent")) {
    return `${msg}. Pick or create a fleet project on this agent tab, or deselect the project and send again (self improvement can run without a project).`;
  }
  return msg;
}

export type FleetSelfImprovementStartResult = {
  job: SelfImprovementJobRecord;
  childSessionKey: string;
  runId: string;
  warning?: string;
  youtrackConfigured?: boolean;
};

export type FleetSelfImprovementResolveWorkspaceResult = {
  agentId: string;
  workerWorkspaceDir: string;
  sessionToolWorkspaceDir?: string;
  defaultAgentWorkspaceDir: string;
  isJaisiuMonorepo?: boolean;
  workerWorkspaceWarning?: string;
  youtrackConfigured?: boolean;
  /** False on OTGS — Control UI must not offer Self improvement ticket creates. */
  selfImprovementEnabled?: boolean;
};

export async function resolveFleetSelfImprovementWorkspace(
  client: GatewayBrowserClient,
  params: {
    agentId: string;
    sessionKey: string;
    workerAgentId?: string;
  },
): Promise<FleetSelfImprovementResolveWorkspaceResult> {
  return client.request<FleetSelfImprovementResolveWorkspaceResult>(
    "fleet.selfImprovement.resolveWorkspace",
    {
      agentId: params.agentId,
      sessionKey: params.sessionKey,
      workerAgentId: params.workerAgentId?.trim() || undefined,
    },
  );
}

export async function startFleetSelfImprovement(
  client: GatewayBrowserClient,
  params: {
    agentId: string;
    sessionKey: string;
    goal: string;
    youtrackProject?: string;
    workerAgentId?: string;
    projectId?: string | null;
  },
): Promise<FleetSelfImprovementStartResult> {
  return client.request<FleetSelfImprovementStartResult>("fleet.selfImprovement.start", {
    agentId: params.agentId,
    sessionKey: params.sessionKey,
    goal: params.goal,
    youtrackProject: params.youtrackProject?.trim() || undefined,
    workerAgentId: params.workerAgentId?.trim() || undefined,
    projectId: params.projectId?.trim() || undefined,
  });
}

export type FleetSelfImprovementStatusResult = {
  job: SelfImprovementJobRecord;
  youtrackConfigured?: boolean;
};

export async function getFleetSelfImprovementStatus(
  client: GatewayBrowserClient,
  params: { agentId: string; jobId: string },
): Promise<FleetSelfImprovementStatusResult> {
  return client.request<FleetSelfImprovementStatusResult>("fleet.selfImprovement.status", {
    agentId: params.agentId,
    jobId: params.jobId,
  });
}
